import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronUp, FiChevronDown, FiExternalLink, FiInfo } = FiIcons;

const ResultsTable = ({ matches, sortBy, sortOrder, onSort }) => {
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? FiChevronUp : FiChevronDown;
  };

  const getSavingsBand = (savings) => {
    if (savings >= 1000000) return 'high';
    if (savings >= 500000) return 'medium';
    return 'low';
  };

  const getBandColor = (band) => {
    switch (band) {
      case 'high': return 'bg-success-50 border-success-200';
      case 'medium': return 'bg-warning-50 border-warning-200';
      case 'low': return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push('★');
      } else if (i === fullStars && hasHalfStar) {
        stars.push('☆');
      } else {
        stars.push('☆');
      }
    }

    return (
      <span className="text-yellow-500 font-medium">
        {stars.join('')} {rating}/5
      </span>
    );
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => onSort(column)}
      className="flex items-center space-x-1 font-medium text-gray-700 hover:text-gray-900 focus-ring rounded px-1 py-1"
    >
      <span>{children}</span>
      {getSortIcon(column) && (
        <SafeIcon icon={getSortIcon(column)} className="w-4 h-4" />
      )}
    </button>
  );

  if (!matches || matches.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No incentive matches found.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
    >
      <div className="table-responsive">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <SortButton column="rank">Rank</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton column="jurisdiction_name">Country/Region</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton column="incentive_type">Type</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton column="estimatedSavings">Est. Savings</SortButton>
              </th>
              <th className="px-4 py-3 text-left">Eligibility Summary</th>
              <th className="px-4 py-3 text-left">
                <SortButton column="mark_stars">Mark</SortButton>
              </th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {matches.map((match, index) => {
              // Display match data

              const savingsBand = getSavingsBand(match.estimatedSavings);
              const bandColor = getBandColor(savingsBand);

              return (
                <motion.tr
                  key={match.id || index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`hover:bg-gray-50 transition-colors border-l-4 ${bandColor}`}
                >
                  <td className="px-4 py-4">
                    <span className="font-bold text-lg text-gray-900">
                      #{match.rank}
                    </span>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {match.jurisdiction_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {match.incentive_name}
                      </p>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {match.incentive_type}
                    </span>
                  </td>
                  
                  <td className="px-4 py-4">
                    <p className="font-bold text-lg text-gray-900">
                      {formatCurrency(match.estimatedSavings, match.currency)}
                    </p>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-600 max-w-xs">
                      <p>{match.summary_one_liner}</p>
                      {match.tooltip && (
                        <div className="mt-1 space-y-1 text-xs">
                          {match.tooltip.rate && (
                            <p><strong>Rate:</strong> {match.tooltip.rate}</p>
                          )}
                          {match.tooltip.min_spend && (
                            <p><strong>Min Spend:</strong> {match.tooltip.min_spend}</p>
                          )}
                          {match.tooltip.cultural_test && (
                            <p><strong>Cultural Test:</strong> Required</p>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    {renderStars(match.mark_stars)}
                  </td>
                  
                  <td className="px-4 py-4">
                    {match.official_url && (
                      <a
                        href={match.official_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-800 focus-ring rounded px-2 py-1"
                      >
                        <span className="text-sm">Learn More</span>
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default ResultsTable;